import "./styles.scss";

import { useParams } from "react-router-dom";

import { useCommunity } from "@contexts/CommunityContext";

import Screen from "@components/Screen";
import {
    AltRouteRounded,
    AutoAwesomeRounded,
    DoNotDisturbAltRounded,
    ExtensionRounded,
    KeyboardArrowDownRounded,
    LocationSearchingRounded,
    SavingsRounded,
    SellRounded,
    SettingsRounded,
} from "@mui/icons-material";

import { Text } from "@fragments";
import { CommunityBanner } from "@components/Community";

import General from './tabs/General';
import Pricing from './tabs/Pricing';
import Coupons from './tabs/Coupons';
import Split from './tabs/Split';
import Pixel from './tabs/Pixel';
import Integrations from './tabs/Integrations';

import { useEffect, useRef, useState } from "react";

import api from "@api";
import { CircularProgress } from "@mui/material";


export default function CommunityConfig() {
    let { tab, slug } = useParams();

    const { loading: communityLoading, community, communities } = useCommunity();

    const [loadingCommunity, setLoadingCommunity] = useState(true);
    const [info, setInfo] = useState(null);
    const [pageActions, setPageActions] = useState(null);

    const currentCommunity = community.getBySlug(slug);

    const ownerCommunities = communities?.filter(
        c => ['owner', 'admin', 'creator'].includes(c.role)
    ) ?? [];

    const items = [
        {
            title: "Geral",
            subtitle: "Nome, descrição, links, etc",
            url: "general",
            icon: <AutoAwesomeRounded />,
            component: <General info={info} setPageActions={setPageActions} />,
            default: true
        },
        {
            title: "Precificação",
            subtitle: "Planos de assinatura",
            url: "pricing",
            icon: <SavingsRounded />,
            component: <Pricing info={info} setPageActions={setPageActions} />
        },
        {
            title: "Cupons de desconto",
            subtitle: "Gere cupons de desconto",
            url: "coupons",
            icon: <SellRounded />,
            component: <Coupons info={info} setPageActions={setPageActions} />
        },
        {
            title: "Split de pagamentos",
            subtitle: "Divida a receita entre recebedores",
            url: "split",
            icon: <AltRouteRounded />,
            component: <Split info={info} setPageActions={setPageActions} />
        },
        {
            title: "Pixel de conversão",
            subtitle: "Rastreie as vendas da sua comunidade",
            url: "pixel",
            icon: <LocationSearchingRounded />,
            component: <Pixel info={info} setPageActions={setPageActions} handleRefresh={() => setLoadingCommunity(true)} />
        },
        {
            title: "Integrações",
            subtitle: "Conecte plataformas externas",
            url: "integrations",
            icon: <ExtensionRounded />,
            component: <Integrations info={info} setPageActions={setPageActions} />
        }
    ];

    const current = items.find(i => i.url === tab) || items[0];

    const hasAccess = ['owner', 'admin', 'creator'].includes(currentCommunity?.role);

    useEffect(() => {
        if (loadingCommunity && currentCommunity?.id) {
            handleLoadCommunity();
        }
    }, [currentCommunity, loadingCommunity]);


    async function handleLoadCommunity() {
        try {
            const { data } = await api.get(`/community?id=${currentCommunity.id}`);
            if (data?.id) setInfo({ ...data });
        } catch (error) {
            console.log(error);
        }
        setLoadingCommunity(false);
    }

    return (
        <Screen fullWidth disablePaddingBottom className="config-community-page">
            <div className="current-config-page">
                <ConfigSidebar
                    items={items}
                    ownerCommunities={ownerCommunities}
                    currentSlug={slug}
                    currentTab={tab}
                />

                <div className="config-content">
                    <div className="config-subheader">
                        <div className="config-subheader-left">
                            <Text.h4 contrast={8} weight={500}>{current.title}</Text.h4>
                        </div>
                        {pageActions && (
                            <div className="config-subheader-actions">{pageActions}</div>
                        )}
                    </div>

                    <div className="config-tab-content">
                        {loadingCommunity ? (
                            <Loading />
                        ) : hasAccess ? (
                            current.component
                        ) : (
                            <NotFound />
                        )}
                    </div>
                </div>
            </div>
        </Screen>
    );
}

function ConfigSidebar({ items, ownerCommunities, currentSlug, currentTab }) {
    const [selectorOpen, setSelectorOpen] = useState(false);
    const selectorRef = useRef(null);

    const currentCommunity = ownerCommunities.find(c => c.slug === currentSlug);

    useEffect(() => {
        function handleClickOutside(e) {
            if (selectorRef.current && !selectorRef.current.contains(e.target)) {
                setSelectorOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    function handleSelectCommunity(c) {
        setSelectorOpen(false);
        window.navigate(c.slug + '/settings/' + (currentTab || ''));
    }

    return (
        <div className="config-community-sidebar">
            <div className="ccs-header">
                <Text.h5 contrast={9} weight={400}>
                    Gerenciar comunidade
                </Text.h5>
            </div>

            <div
                ref={selectorRef}
                className={`ccs-selector ${selectorOpen ? 'open' : ''}`}
                onClick={() => setSelectorOpen(v => !v)}
            >
                <div className="ccs-selector-trigger">
                    <CommunityBanner
                        small
                        color="white"
                        className="ccs-selector-banner"
                        banner={currentCommunity?.banner}
                    />
                    <Text.h6 contrast={6} grow className="ccs-selector-name">
                        {currentCommunity?.name ?? '—'}
                    </Text.h6>
                    <KeyboardArrowDownRounded className={`ccs-chevron ${selectorOpen ? 'open' : ''}`} />
                </div>

                {selectorOpen && ownerCommunities.length > 0 && (
                    <div className="ccs-selector-dropdown">
                        {ownerCommunities.map(c => (
                            <div
                                key={c.id}
                                className={`ccs-selector-option ${c.slug === currentSlug ? 'active' : ''}`}
                                onClick={e => { e.stopPropagation(); handleSelectCommunity(c); }}
                            >
                                <CommunityBanner
                                    small
                                    color="white"
                                    className="ccs-selector-banner"
                                    banner={c.banner}
                                />
                                <Text.span contrast={2}>{c.name}</Text.span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <div className="ccs-menu mt-2">
                {items.map((item, index) => (
                    <SidebarButton
                        key={index}
                        {...item}
                        currentTab={currentTab}
                        currentSlug={currentSlug}
                    />
                ))}
            </div>
        </div>
    );
}

function SidebarButton({ title, subtitle, icon, url, component, currentTab, currentSlug, ...props }) {
    const isActive = currentTab === url || (props.default && !currentTab);
    const isDisabled = !component;

    return (
        <div
            className={`ccs-menu-btn ${isActive ? 'active' : ''} ${isDisabled ? 'disabled' : ''}`}
            onClick={() => !isDisabled && window.navigate(currentSlug + '/settings/' + url)}
        >
            <span className="ccs-menu-btn-icon">{icon}</span>
            <div className="ccs-menu-btn-desc">
                <Text.h6 contrast={isActive ? 8 : 7}>{title}</Text.h6>
                <Text.span contrast={isActive ? 7 : 6}>{subtitle}</Text.span>
            </div>
        </div>
    );
}

function Loading() {
    return (
        <div className="config-loading">
            <CircularProgress size={20} />
            <Text.span contrast={5}>Carregando...</Text.span>
        </div>
    );
}

function NotFound() {
    return (
        <div className="config-not-found">
            <DoNotDisturbAltRounded fontSize="large" />
            <Text.h2 contrast={3}>Comunidade não encontrada</Text.h2>
        </div>
    );
}
